import React, { useEffect, useState } from "react";
import {
  Box,
  IconButton,
  Avatar,
  Typography,
  Tooltip,
  Menu,
  MenuItem,
  Divider,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import defaultLogo from "../../assets/super_admin/logo/logo.png";

export default function SuperAdminHeader({ onToggle }) {
  const [userName, setUserName] = useState("");
  const [avatarSrc, setAvatarSrc] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [role, setRole] = useState("");

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    try {
      setUserName(localStorage.getItem("name") || "Super Admin");
      setRole(localStorage.getItem("role") || "owner");
      const avatar = localStorage.getItem("avatar");
      if (avatar) setAvatarSrc(avatar);
    } catch (e) {
      console.warn("Header init error", e);
    }
  }, []);

  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = "/super_admin/login";
  };

  return (
    <Box
      component="header"
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        height: 64,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: { xs: 1.5, sm: 3, md: 4 },
        bgcolor: "rgba(255,255,255,0.9)",
        borderBottom: "1px solid rgba(37,99,235,0.12)",
        zIndex: 1400,
        boxShadow: "0 10px 32px rgba(23,32,51,0.1)",
        backdropFilter: "saturate(130%) blur(14px)",
      }}
    >
      {/* Left side - toggle + logo */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <IconButton
          onClick={() => typeof onToggle === "function" && onToggle()}
          sx={{
            color: "#102a43",
            bgcolor: "rgba(37,99,235,0.08)",
            border: "1px solid rgba(37,99,235,0.12)",
          }}
        >
          <MenuIcon />
        </IconButton>
        <Avatar
          src={defaultLogo}
          alt="platform logo"
          sx={{
            width: 100,
            height: 50,
            borderRadius: 1.5,
            border: "1px solid rgba(37,99,235,0.14)",
            boxShadow: "0 10px 24px rgba(23,32,51,0.12)",
            transition: "transform .18s ease, box-shadow .18s ease",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: "0 14px 30px rgba(23,32,51,0.16)",
            },
          }}
        />
      </Box>

      {/* Middle - title */}
      <Box sx={{ flex: 1, textAlign: "center", px: 2 }}>
        <Typography
          variant="h6"
          noWrap
          sx={{
            fontWeight: 700,
            letterSpacing: 0.3,
            fontSize: { xs: "0.95rem", sm: "1.05rem", md: "1.15rem" },
            color: "#102a43",
          }}
        >
          PLATFORM ADMIN PANEL
        </Typography>
      </Box>

      {/* Right side - user info */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {!isMobile && (
          <Typography
            variant="body2"
            sx={{ fontWeight: 800, mr: 1, maxWidth: 140, color: "#102a43" }}
            noWrap
          >
            {userName}
          </Typography>
        )}

        <Tooltip title="Account">
          <IconButton
            onClick={handleMenuOpen}
            sx={{
              p: 0.35,
              border: "1px solid rgba(37,99,235,0.14)",
              bgcolor: "rgba(255,255,255,0.72)",
            }}
          >
            <Avatar
              src={avatarSrc || undefined}
              alt={userName}
              sx={{ width: 40, height: 40, bgcolor: "#2563eb" }}
            >
              {!avatarSrc && <AccountCircleIcon />}
            </Avatar>
          </IconButton>
        </Tooltip>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            sx: {
              minWidth: 190,
              p: 0,
              borderRadius: 2,
              overflow: "hidden",
              border: "1px solid rgba(78,102,135,0.16)",
              boxShadow: "0 18px 46px rgba(23,32,51,0.16)",
            },
          }}
        >
          <Box sx={{ px: 2, py: 1.25 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {userName}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "text.secondary" }}
              noWrap
            >
              {role}
            </Typography>
          </Box>

          <Divider sx={{ my: 0 }} />

          <MenuItem sx={{ gap: 1 }}>
            <AccountCircleIcon fontSize="small" />
            <Typography variant="body2">Profile</Typography>
          </MenuItem>

          <MenuItem
            onClick={() => {
              handleMenuClose();
              handleLogout();
            }}
            sx={{ gap: 1 }}
          >
            <LogoutIcon fontSize="small" />
            <Typography variant="body2">Logout</Typography>
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
}
